import { useEffect, useMemo, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import {
  Map as MapIcon, Clock, Euro, PlayCircle, X, Loader2, MapPin, Mic, MicOff, BarChart3, PlusCircle, Building2, Wrench, FileText, Trash2,
} from "lucide-react";
import {
  isWakeWordEnabled, setWakeWordEnabled, getLastHeard, matchesWakePhrase,
} from "../components/WakeWordListener";

// Iter123k202 · Mappa Sopralluoghi CoPilot (Leaflet via CDN).
// Pin colorati per durata: verde <30min, giallo 30-90min, rosso >90min.

const LEAFLET_CSS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.css";
const LEAFLET_JS = "https://unpkg.com/leaflet@1.9.4/dist/leaflet.js";

const loadLeaflet = () => new Promise((resolve, reject) => {
  if (window.L) return resolve(window.L);
  if (!document.querySelector(`link[href="${LEAFLET_CSS}"]`)) {
    const link = document.createElement("link");
    link.rel = "stylesheet"; link.href = LEAFLET_CSS;
    document.head.appendChild(link);
  }
  if (document.querySelector(`script[src="${LEAFLET_JS}"]`)) {
    const check = setInterval(() => {
      if (window.L) { clearInterval(check); resolve(window.L); }
    }, 100);
    setTimeout(() => { clearInterval(check); if (!window.L) reject(new Error("Leaflet timeout")); }, 10000);
    return;
  }
  const script = document.createElement("script");
  script.src = LEAFLET_JS;
  script.onload = () => resolve(window.L);
  script.onerror = reject;
  document.head.appendChild(script);
});

const colorForDurata = (min) => {
  if (min == null) return "#64748b";
  if (min < 30) return "#059669";    // emerald-600
  if (min <= 90) return "#eab308";   // yellow-500
  return "#dc2626";                   // red-600
};

const labelDurata = (min) => {
  if (!min) return "—";
  const h = Math.floor(min / 60); const mm = min % 60;
  return h > 0 ? `${h}h ${mm}m` : `${mm}m`;
};

export default function CoPilotMappa() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";
  const [sessioni, setSessioni] = useState([]);
  const [appalti, setAppalti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [starting, setStarting] = useState(false);
  const [wakeOn, setWakeOn] = useState(isWakeWordEnabled());
  const [showLibero, setShowLibero] = useState(false);
  const [liberoTitolo, setLiberoTitolo] = useState("");
  const [liberoLuogo, setLiberoLuogo] = useState("");
  const [heardText, setHeardText] = useState(getLastHeard());
  const [showDebug, setShowDebug] = useState(false);
  // Autocomplete condomini + pratiche collegate
  const [condomini, setCondomini] = useState([]);
  const [liberoCondominioId, setLiberoCondominioId] = useState(null);
  const [liberoCondominioNome, setLiberoCondominioNome] = useState("");
  const [pratiche, setPratiche] = useState({ tickets: [], preventivi: [] });
  const [loadingPratiche, setLoadingPratiche] = useState(false);
  const [praticaSel, setPraticaSel] = useState(null); // {tipo:'ticket'|'preventivo', id, titolo}
  const [showCondSuggest, setShowCondSuggest] = useState(false);
  // Conferma eliminazione sopralluoghi (toggle inline per riga)
  const [confirmDelId, setConfirmDelId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const mapRef = useRef(null);
  const mapDivRef = useRef(null);
  const markersRef = useRef([]);

  // Load dati
  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [s, a, c] = await Promise.all([
          api.get("/copilot/sessioni"),
          api.get("/copilot/appalti-recenti"),
          api.get("/copilot/condomini"),
        ]);
        setSessioni(s.data || []);
        setAppalti(a.data || []);
        setCondomini(c.data || []);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento");
      }
      setLoading(false);
    })();
  }, []);

  // Se arriva da wake word → apri il picker
  useEffect(() => {
    const p = new URLSearchParams(location.search);
    if (p.get("wake") === "1") {
      setShowPicker(true);
      // Pulisco il parametro
      navigate("/copilot/mappa", { replace: true });
    }
  }, [location.search, navigate]);

  // Aggiornamento live della diagnostica wake word
  useEffect(() => {
    const onHeard = (e) => setHeardText(e.detail?.text || "");
    window.addEventListener("wake-word-heard", onHeard);
    return () => window.removeEventListener("wake-word-heard", onHeard);
  }, []);

  const sessioniConGeo = useMemo(
    () => sessioni.filter((s) => typeof s.lat === "number" && typeof s.lng === "number"),
    [sessioni],
  );

  // Init mappa Leaflet
  useEffect(() => {
    let L;
    let destroyed = false;
    (async () => {
      try {
        L = await loadLeaflet();
      } catch {
        toast.error("Mappa non disponibile (connessione?)");
        return;
      }
      if (destroyed || !mapDivRef.current) return;
      if (!mapRef.current) {
        mapRef.current = L.map(mapDivRef.current, { zoomControl: true, scrollWheelZoom: true });
        L.tileLayer("https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png", {
          maxZoom: 19,
          attribution: '&copy; OpenStreetMap',
        }).addTo(mapRef.current);
        // Default view: centro Italia
        mapRef.current.setView([41.9, 12.5], 6);
      }
      // Pulisco marker precedenti
      markersRef.current.forEach((m) => mapRef.current.removeLayer(m));
      markersRef.current = [];
      if (sessioniConGeo.length === 0) return;
      const bounds = [];
      sessioniConGeo.forEach((s) => {
        const color = colorForDurata(s.durata_min);
        const icon = L.divIcon({
          className: "copilot-pin",
          html: `<div style="background:${color};width:22px;height:22px;border-radius:50% 50% 50% 0;transform:rotate(-45deg);border:2px solid white;box-shadow:0 2px 4px rgba(0,0,0,0.3);"></div>`,
          iconSize: [22, 22], iconAnchor: [11, 22],
        });
        const marker = L.marker([s.lat, s.lng], { icon }).addTo(mapRef.current);
        const d = (s.started_at || "").slice(0, 10);
        marker.bindPopup(
          `<div style="font-family:system-ui;min-width:180px">
            <div style="font-weight:700;color:#7A193D">${(s.condominio_nome || "—")}</div>
            <div style="font-size:12px;color:#64748b;margin-bottom:6px">${s.appalto_titolo || ""}</div>
            <div style="font-size:12px">📅 ${d}</div>
            <div style="font-size:12px">⏱️ ${labelDurata(s.durata_min)}</div>
            <div style="font-size:12px">💶 € ${(s.costo_totale || 0).toFixed(2)}</div>
            <a href="/copilot/${s.id}" style="display:block;margin-top:6px;color:#7A193D;font-weight:600;text-decoration:underline">Apri sessione →</a>
          </div>`,
        );
        bounds.push([s.lat, s.lng]);
        markersRef.current.push(marker);
      });
      if (bounds.length === 1) mapRef.current.setView(bounds[0], 14);
      else if (bounds.length > 1) mapRef.current.fitBounds(bounds, { padding: [40, 40] });
    })();
    return () => {
      destroyed = true;
    };
  }, [sessioniConGeo]);

  // Cleanup mappa su unmount
  useEffect(() => () => {
    if (mapRef.current) { mapRef.current.remove(); mapRef.current = null; }
  }, []);

  const toggleWake = () => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) { toast.error("Il tuo browser non supporta il riconoscimento vocale (usa Chrome/Edge)"); return; }
    const v = !wakeOn;
    setWakeWordEnabled(v);
    setWakeOn(v);
    toast.success(v ? "Ascolto Ginius attivato (dì 'Hei Ginius attiva CoPilot')" : "Ascolto Ginius disattivato");
  };

  const avviaSessione = async (payload) => {
    setStarting(true);
    // Prova a prendere la posizione (max 5s)
    const coord = await new Promise((resolve) => {
      if (!navigator.geolocation) return resolve(null);
      let done = false;
      const timer = setTimeout(() => { if (!done) { done = true; resolve(null); } }, 5000);
      navigator.geolocation.getCurrentPosition(
        (p) => { if (!done) { done = true; clearTimeout(timer); resolve({ lat: p.coords.latitude, lng: p.coords.longitude }); } },
        () => { if (!done) { done = true; clearTimeout(timer); resolve(null); } },
        { enableHighAccuracy: true, timeout: 5000, maximumAge: 30000 },
      );
    });
    try {
      const body = { ...payload };
      if (coord) { body.lat = coord.lat; body.lng = coord.lng; }
      const r = await api.post("/copilot/sessioni", body);
      toast.success("Sopralluogo avviato");
      navigate(`/copilot/${r.data.id}`);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Avvio fallito");
      setStarting(false);
    }
  };

  const avviaDaAppalto = (appaltoId) => avviaSessione({ appalto_id: appaltoId });

  const eliminaSessione = async (sid) => {
    setDeletingId(sid);
    try {
      await api.delete(`/copilot/sessioni/${sid}`);
      setSessioni((prev) => prev.filter((s) => s.id !== sid));
      toast.success("Sopralluogo eliminato");
    } catch (e) {
      const code = e?.response?.status;
      if (code === 403) {
        toast.error("Solo l'admin dello studio può eliminare i sopralluoghi");
      } else if (code === 404) {
        toast.error("Sopralluogo non trovato");
      } else {
        toast.error(formatApiError(e.response?.data?.detail) || "Eliminazione fallita");
      }
    }
    setDeletingId(null);
    setConfirmDelId(null);
  };

  const avviaLibero = () => {
    const titolo = liberoTitolo.trim();
    if (!titolo) { toast.error("Serve almeno un titolo"); return; }
    const payload = { titolo };
    if (liberoCondominioId) {
      payload.condominio_id = liberoCondominioId;
    } else if (liberoLuogo.trim()) {
      payload.condominio_nome = liberoLuogo.trim();
    }
    if (praticaSel?.tipo === "ticket") payload.ticket_id = praticaSel.id;
    if (praticaSel?.tipo === "preventivo") payload.preventivo_id = praticaSel.id;
    avviaSessione(payload);
  };

  // Autocomplete filtrato dei condomini (contains, case-insensitive).
  // Nessun cap: mostriamo l'intera lista (il dropdown è scrollabile via max-h/overflow-y-auto).
  const condSuggestions = useMemo(() => {
    const q = (liberoLuogo || "").toLowerCase().trim();
    if (!q) return condomini;
    return condomini.filter(
      (c) => c.nome.toLowerCase().includes(q) ||
             (c.indirizzo || "").toLowerCase().includes(q),
    );
  }, [liberoLuogo, condomini]);

  const selezionaCondominio = async (c) => {
    setLiberoCondominioId(c.id);
    setLiberoCondominioNome(c.nome);
    setLiberoLuogo(c.nome);
    setShowCondSuggest(false);
    setPraticaSel(null);
    setLoadingPratiche(true);
    setPratiche({ tickets: [], preventivi: [] });
    try {
      const r = await api.get(`/copilot/condomini/${c.id}/pratiche`);
      setPratiche({ tickets: r.data.tickets || [], preventivi: r.data.preventivi || [] });
    } catch (e) {
      toast.error("Errore caricamento pratiche");
    }
    setLoadingPratiche(false);
  };

  const resetCondominio = () => {
    setLiberoCondominioId(null);
    setLiberoCondominioNome("");
    setLiberoLuogo("");
    setPratiche({ tickets: [], preventivi: [] });
    setPraticaSel(null);
  };

  const closePicker = () => {
    if (starting) return;
    setShowPicker(false);
    setShowLibero(false);
    setLiberoTitolo("");
    setLiberoLuogo("");
    setLiberoCondominioId(null);
    setLiberoCondominioNome("");
    setPratiche({ tickets: [], preventivi: [] });
    setPraticaSel(null);
    setShowCondSuggest(false);
  };

  const SR_supported = typeof window !== "undefined" && (window.SpeechRecognition || window.webkitSpeechRecognition);

  return (
    <div className="space-y-4" data-testid="copilot-mappa-page">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-2xl sm:text-3xl font-bold text-brand flex items-center gap-2">
          <MapIcon className="w-6 h-6 sm:w-7 sm:h-7" /> Mappa Sopralluoghi
        </h1>
        <div className="flex flex-wrap gap-2 w-full sm:w-auto justify-end">
          {SR_supported && (
            <Button data-testid="wake-toggle-btn" onClick={toggleWake}
              className={`${wakeOn
                ? "bg-emerald-600 hover:bg-emerald-700 text-white"
                : "bg-slate-200 hover:bg-slate-300 text-slate-800"} px-3`}>
              {wakeOn ? <Mic className="w-4 h-4 sm:mr-1" /> : <MicOff className="w-4 h-4 sm:mr-1" />}
              <span className="hidden sm:inline">{wakeOn ? "Ginius in ascolto" : "Attiva voce Ginius"}</span>
              <span className="sm:hidden ml-1 text-xs">{wakeOn ? "Attivo" : "Voce"}</span>
            </Button>
          )}
          <Button data-testid="copilot-stats-link" onClick={() => navigate("/copilot/statistiche")}
            className="bg-slate-700 hover:bg-slate-800 text-white px-3">
            <BarChart3 className="w-4 h-4 sm:mr-1" />
            <span className="hidden sm:inline">Statistiche</span>
            <span className="sm:hidden ml-1 text-xs">Stat.</span>
          </Button>
          <Button data-testid="copilot-nuovo-btn" onClick={() => setShowPicker(true)}
            className="bg-brand hover:bg-brand/90 text-white px-3 flex-1 sm:flex-none">
            <PlayCircle className="w-4 h-4 mr-1" />
            <span className="hidden sm:inline">Nuovo sopralluogo</span>
            <span className="sm:hidden text-sm font-semibold">Nuovo sopralluogo</span>
          </Button>
        </div>
      </div>

      {SR_supported && wakeOn && (
        <div data-testid="wake-hint" className="rounded-lg bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm px-3 py-2 space-y-1">
          <div>
            Microfono attivo in ascolto. Dì <b>"Hei Ginius attiva CoPilot"</b> per avviare un sopralluogo a mani libere.
          </div>
          <button data-testid="wake-debug-toggle"
            onClick={() => setShowDebug((v) => !v)}
            className="text-xs underline text-emerald-700 hover:text-emerald-900">
            {showDebug ? "Nascondi diagnostica" : "Non funziona? Mostra diagnostica"}
          </button>
          {showDebug && (
            <div data-testid="wake-debug-panel" className="mt-1 bg-white border border-emerald-300 rounded p-2 text-xs text-slate-700 space-y-1">
              <div className="font-bold text-emerald-800">Diagnostica voce</div>
              <div className="opacity-80">
                Ultima frase sentita dal microfono (aggiornato live):
              </div>
              <div data-testid="wake-last-heard"
                className="bg-slate-50 rounded px-2 py-1 font-mono text-[11px] break-words min-h-[28px]">
                {heardText || <span className="italic opacity-60">— nessun audio catturato ancora. Parla ad alta voce vicino al microfono —</span>}
              </div>
              {heardText && (
                <div className={`font-semibold ${matchesWakePhrase(heardText) ? "text-emerald-700" : "text-amber-700"}`}>
                  {matchesWakePhrase(heardText)
                    ? "✓ Frase riconosciuta come trigger"
                    : "✗ Frase NON riconosciuta — prova a dire più chiaramente 'Ginius copilot'"}
                </div>
              )}
              <div className="opacity-80 pt-1 border-t border-slate-200">
                Suggerimento: Chrome a volte scrive "Ginius" come <i>genius</i>, <i>junius</i>, <i>ginnius</i>. Il sistema accetta tutte queste varianti. Basta dire <b>"Ginius copilot"</b> (anche senza "Hei" e "attiva").
              </div>
              <Button data-testid="wake-test-trigger"
                size="sm"
                onClick={() => navigate("/copilot/mappa?wake=1")}
                className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white mt-1">
                Prova trigger manuale
              </Button>
            </div>
          )}
        </div>
      )}

      {/* Legenda */}
      <div className="flex flex-wrap gap-3 text-xs text-slate-600">
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-emerald-600" /> &lt; 30 min</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-yellow-500" /> 30-90 min</span>
        <span className="flex items-center gap-1"><span className="inline-block w-3 h-3 rounded-full bg-red-600" /> &gt; 90 min</span>
        <span className="ml-2 opacity-70">
          {sessioniConGeo.length} di {sessioni.length} sessioni con posizione
        </span>
      </div>

      {/* Mappa */}
      <Card className="p-0 overflow-hidden">
        <div ref={mapDivRef} data-testid="copilot-mappa-canvas"
          className="w-full h-72 sm:h-[480px]"
          style={{ background: "#e2e8f0" }} />
        {sessioniConGeo.length === 0 && !loading && (
          <div className="text-center text-sm text-slate-500 py-3 px-2">
            Nessun sopralluogo geolocalizzato. Quando avvii un nuovo sopralluogo e concedi la posizione, apparirà qui.
          </div>
        )}
      </Card>

      {/* Elenco sessioni (anche senza geo) */}
      <Card className="p-3">
        <div className="font-bold text-slate-700 mb-2">Ultimi sopralluoghi</div>
        {loading ? (
          <div className="text-center text-slate-400 py-4"><Loader2 className="inline w-4 h-4 animate-spin mr-1" /> Caricamento…</div>
        ) : sessioni.length === 0 ? (
          <div className="text-center text-slate-400 py-4">Nessun sopralluogo ancora.</div>
        ) : (
          <div className="divide-y">
            {sessioni.slice(0, 20).map((s) => {
              const confirming = confirmDelId === s.id;
              const deleting = deletingId === s.id;
              return (
                <div key={s.id} data-testid={`copilot-sess-${s.id}`}
                  className="flex items-center gap-3 py-2 hover:bg-slate-50 rounded px-2">
                  <span className="inline-block w-3 h-3 rounded-full shrink-0"
                    style={{ background: colorForDurata(s.durata_min) }} />
                  <div className="flex-1 min-w-0 cursor-pointer"
                    onClick={() => navigate(`/copilot/${s.id}`)}>
                    <div className="font-semibold truncate">{s.condominio_nome || "—"}</div>
                    <div className="text-xs text-slate-500 truncate">{s.appalto_titolo}</div>
                  </div>
                  <div className="text-xs text-slate-500 whitespace-nowrap flex items-center gap-2 shrink-0">
                    <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {labelDurata(s.durata_min)}</span>
                    <span className="flex items-center gap-1"><Euro className="w-3 h-3" /> {(s.costo_totale || 0).toFixed(0)}</span>
                    {s.lat && s.lng && <MapPin className="w-3 h-3 text-emerald-600" />}
                    {isAdmin && (
                      confirming ? (
                        <div className="flex items-center gap-1 ml-1" onClick={(e) => e.stopPropagation()}>
                          <button
                            data-testid={`copilot-sess-del-confirm-${s.id}`}
                            onClick={() => eliminaSessione(s.id)}
                            disabled={deleting}
                            className="px-2 py-0.5 rounded bg-red-600 hover:bg-red-700 text-white text-[11px] font-semibold flex items-center gap-1">
                            {deleting
                              ? <Loader2 className="w-3 h-3 animate-spin" />
                              : <Trash2 className="w-3 h-3" />}
                            Conferma
                          </button>
                          <button
                            data-testid={`copilot-sess-del-cancel-${s.id}`}
                            onClick={() => setConfirmDelId(null)}
                            disabled={deleting}
                            className="px-1.5 py-0.5 rounded text-slate-500 hover:text-slate-700 text-[11px]">
                            Annulla
                          </button>
                        </div>
                      ) : (
                        <button
                          data-testid={`copilot-sess-del-${s.id}`}
                          onClick={(e) => { e.stopPropagation(); setConfirmDelId(s.id); }}
                          title="Elimina sopralluogo"
                          className="ml-1 p-1 rounded hover:bg-red-50 text-slate-400 hover:text-red-600 transition-colors">
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Picker appalto */}
      {showPicker && (
        <div className="fixed inset-0 bg-black/50 z-[1100] flex items-end sm:items-center justify-center p-4"
          onClick={closePicker}>
          <Card className="w-full max-w-md p-4 max-h-[85vh] overflow-y-auto"
            onClick={(e) => e.stopPropagation()} data-testid="copilot-appalto-picker">
            <div className="flex items-center justify-between mb-3">
              <div className="font-bold text-lg">Avvia sopralluogo</div>
              <button onClick={closePicker}
                className="p-1 hover:bg-slate-100 rounded-full" data-testid="copilot-picker-close">
                <X className="w-5 h-5" />
              </button>
            </div>
            {starting ? (
              <div className="text-center py-6 text-slate-500">
                <Loader2 className="inline w-5 h-5 animate-spin mr-1" /> Avvio sopralluogo…
              </div>
            ) : (
              <>
                {/* Sopralluogo libero */}
                {!showLibero ? (
                  <button data-testid="copilot-libero-toggle"
                    onClick={() => setShowLibero(true)}
                    className="w-full flex items-center gap-2 p-3 rounded-lg border-2 border-dashed border-brand/50 bg-brand/5 hover:bg-brand/10 text-left mb-3">
                    <PlusCircle className="w-5 h-5 text-brand shrink-0" />
                    <div>
                      <div className="font-semibold text-brand">Sopralluogo libero</div>
                      <div className="text-xs text-slate-500">Non collegato ad alcun appalto esistente</div>
                    </div>
                  </button>
                ) : (
                  <Card className="p-3 mb-3 bg-brand/5 border-2 border-brand/40"
                    data-testid="copilot-libero-form">
                    <div className="flex items-center justify-between mb-2">
                      <div className="font-semibold text-brand text-sm">Nuovo sopralluogo libero</div>
                      <button onClick={() => { setShowLibero(false); resetCondominio(); }}
                        className="text-xs text-slate-500 hover:underline"
                        data-testid="copilot-libero-cancel">Annulla</button>
                    </div>
                    <input data-testid="copilot-libero-titolo" value={liberoTitolo}
                      onChange={(e) => setLiberoTitolo(e.target.value)}
                      placeholder="Titolo (es. Verifica infiltrazione via Roma 10)"
                      autoFocus
                      className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm mb-2 focus:border-brand focus:outline-none" />

                    {/* Autocomplete condomini */}
                    <div className="relative mb-2">
                      {liberoCondominioId ? (
                        <div className="flex items-center gap-2 bg-emerald-50 border-2 border-emerald-300 rounded-lg px-2 py-1.5"
                          data-testid="copilot-libero-condominio-selected">
                          <Building2 className="w-4 h-4 text-emerald-700 shrink-0" />
                          <span className="flex-1 text-sm font-semibold text-emerald-900 truncate">
                            {liberoCondominioNome}
                          </span>
                          <button onClick={resetCondominio}
                            data-testid="copilot-libero-condominio-reset"
                            className="text-xs text-emerald-700 hover:underline">
                            Cambia
                          </button>
                        </div>
                      ) : (
                        <>
                          <input data-testid="copilot-libero-luogo" value={liberoLuogo}
                            onChange={(e) => { setLiberoLuogo(e.target.value); setShowCondSuggest(true); }}
                            onFocus={() => setShowCondSuggest(true)}
                            onBlur={() => setTimeout(() => setShowCondSuggest(false), 200)}
                            placeholder="Scegli condominio o scrivi un luogo libero…"
                            className="w-full rounded-lg border-2 border-slate-200 p-2 text-sm focus:border-brand focus:outline-none" />
                          {showCondSuggest && condSuggestions.length > 0 && (
                            <div data-testid="copilot-libero-cond-suggest"
                              className="absolute z-10 left-0 right-0 mt-1 bg-white border-2 border-brand/30 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                              {condSuggestions.map((c) => (
                                <button key={c.id}
                                  data-testid={`copilot-libero-cond-opt-${c.id}`}
                                  onMouseDown={(e) => { e.preventDefault(); selezionaCondominio(c); }}
                                  className="w-full text-left px-2 py-1.5 hover:bg-brand/10 text-sm border-b border-slate-100 last:border-0">
                                  <div className="font-semibold truncate">{c.nome}</div>
                                  {c.indirizzo && <div className="text-xs text-slate-500 truncate">{c.indirizzo}</div>}
                                </button>
                              ))}
                              <div data-testid="copilot-libero-cond-count"
                                className="sticky bottom-0 bg-slate-50 border-t border-slate-200 px-2 py-1 text-[11px] text-slate-500 text-center">
                                {condSuggestions.length} {condSuggestions.length === 1 ? "condominio" : "condomini"}
                                {liberoLuogo.trim() ? "" : ` · scorri per vederli tutti`}
                              </div>
                            </div>
                          )}
                        </>
                      )}
                    </div>

                    {/* Pratiche del condominio selezionato */}
                    {liberoCondominioId && (
                      <div className="mb-2" data-testid="copilot-libero-pratiche">
                        <div className="text-xs font-bold uppercase text-slate-500 mb-1">
                          Collega una pratica (facoltativo)
                        </div>
                        {loadingPratiche ? (
                          <div className="text-xs text-slate-400 py-2 text-center">
                            <Loader2 className="inline w-3 h-3 animate-spin mr-1" /> Caricamento…
                          </div>
                        ) : (pratiche.tickets.length === 0 && pratiche.preventivi.length === 0) ? (
                          <div className="text-xs text-slate-400 py-2 text-center">
                            Nessuna pratica trovata per questo condominio.
                          </div>
                        ) : (
                          <div className="space-y-1 max-h-40 overflow-y-auto bg-white rounded border border-slate-200 p-1">
                            {pratiche.tickets.map((t) => {
                              const sel = praticaSel?.tipo === "ticket" && praticaSel.id === t.id;
                              return (
                                <button key={`t-${t.id}`}
                                  data-testid={`copilot-libero-prat-t-${t.id}`}
                                  onClick={() => setPraticaSel(sel ? null : { tipo: "ticket", id: t.id, titolo: t.titolo })}
                                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-1.5 ${sel ? "bg-brand text-white" : "hover:bg-slate-100"}`}>
                                  <Wrench className={`w-3 h-3 shrink-0 ${sel ? "text-white" : "text-amber-600"}`} />
                                  <span className="font-semibold shrink-0">Ticket</span>
                                  <span className="truncate flex-1">{t.titolo}</span>
                                  {t.stato && <span className={`shrink-0 text-[10px] px-1 rounded ${sel ? "bg-white/20" : "bg-slate-200"}`}>{t.stato}</span>}
                                </button>
                              );
                            })}
                            {pratiche.preventivi.map((p) => {
                              const sel = praticaSel?.tipo === "preventivo" && praticaSel.id === p.id;
                              return (
                                <button key={`p-${p.id}`}
                                  data-testid={`copilot-libero-prat-p-${p.id}`}
                                  onClick={() => setPraticaSel(sel ? null : { tipo: "preventivo", id: p.id, titolo: p.titolo })}
                                  className={`w-full text-left px-2 py-1 rounded text-xs flex items-center gap-1.5 ${sel ? "bg-brand text-white" : "hover:bg-slate-100"}`}>
                                  <FileText className={`w-3 h-3 shrink-0 ${sel ? "text-white" : "text-blue-600"}`} />
                                  <span className="font-semibold shrink-0">Preventivo</span>
                                  <span className="truncate flex-1">{p.titolo}</span>
                                  {p.stato && <span className={`shrink-0 text-[10px] px-1 rounded ${sel ? "bg-white/20" : "bg-slate-200"}`}>{p.stato}</span>}
                                </button>
                              );
                            })}
                          </div>
                        )}
                        {praticaSel && (
                          <div data-testid="copilot-libero-prat-selected"
                            className="mt-1 text-xs text-brand font-semibold flex items-center justify-between gap-2">
                            <span className="truncate">✓ {praticaSel.tipo}: {praticaSel.titolo}</span>
                            <button onClick={() => setPraticaSel(null)}
                              className="text-slate-500 hover:underline">rimuovi</button>
                          </div>
                        )}
                      </div>
                    )}

                    <Button data-testid="copilot-libero-start"
                      onClick={avviaLibero} disabled={!liberoTitolo.trim()}
                      className="w-full bg-brand hover:bg-brand/90 text-white">
                      <PlayCircle className="w-4 h-4 mr-1" /> Avvia sopralluogo libero
                    </Button>
                  </Card>
                )}

                {/* Appalti esistenti */}
                <div className="text-xs font-bold uppercase text-slate-500 mb-1">
                  …oppure da un appalto esistente
                </div>
                {appalti.length === 0 ? (
                  <div className="text-center text-slate-400 py-4 text-sm">
                    Nessun appalto trovato.
                  </div>
                ) : (
                  <div className="divide-y">
                    {appalti.map((a) => (
                      <button key={a.id} data-testid={`copilot-appalto-${a.id}`}
                        onClick={() => avviaDaAppalto(a.id)}
                        className="w-full text-left py-2 px-2 hover:bg-slate-50 rounded">
                        <div className="font-semibold">{a.titolo}</div>
                        <div className="text-xs text-slate-500">{a.condominio_nome}</div>
                      </button>
                    ))}
                  </div>
                )}
              </>
            )}
          </Card>
        </div>
      )}
    </div>
  );
}
